import { useState, useEffect, useContext, useMemo } from 'react';
import { Box, Container } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { Check } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttFormSearch,
    SttList,
    SttNoResults,
} from '../../../sporttia/all';
import { useCrud } from '../../../lib/hooks';
import { getPath } from '../../Pages';
import { formatPriceByLocale } from '../../../lib/utils';

type PriceShape = {
    currency?: string;
    locale?: string;
};

type Bono = {
    id: number;
    name: string;
    price?: number;
    priceShape?: PriceShape;
    purchaseOnline?: boolean;
    type?: string;
};

type BonosResponse = {
    rows?: Bono[];
    count?: number;
};

export default function BonoList(): React.ReactElement | null {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();

    const [bonos, setBonos, , Get] = useCrud<BonosResponse>();
    const [idSC, setIdSC] = useState<string | null>(null);

    const [search, setSearch] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (id) {
            setIdSC(id);
            loadBonos();
        }

        cxt?.setHeader({ title: t('bono.bonos') });
    }, []);

    function loadBonos(params = { rows: 1000 }) {
        Get(`/scs/${id}/bonos`, { ...params }).then((response) =>
            setBonos(response as BonosResponse),
        );
    }

    const filteredBonos = useMemo(() => {
        if (!search) return bonos?.rows;

        return bonos?.rows?.filter((bono) =>
            bono.name.toLowerCase().includes(search.toLowerCase()),
        );
    }, [bonos, search]);

    const handleSearchChange = ({
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }) => {
        setSearch(value as string);
    };

    const handleSearchClean = () => {
        setSearch(undefined);
    };

    if (!bonos || !idSC) {
        return null;
    }

    return (
        <Container maxWidth="md">
            <Box p={1}>
                <SttFormSearch
                    caption={t('general.search')}
                    onChange={handleSearchChange}
                    defVal={search ?? ''}
                    onClean={handleSearchClean}
                />
            </Box>
            <Box p={1}>
                {filteredBonos && filteredBonos.length > 0 ? (
                    <SttList
                        paper
                        title={t('bono.bonos')}
                        onSelect={(bono: Bono) => {
                            if (bono.purchaseOnline) {
                                navigate(
                                    getPath('buyBono', {
                                        state: { idSC, id: bono.id },
                                    }),
                                );
                            }
                        }}
                        data={filteredBonos.map((bono) => ({
                            caption: (
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    gap={2}
                                >
                                    <Box
                                        component="span"
                                        color={
                                            bono.purchaseOnline
                                                ? 'var(--brand-text)'
                                                : 'text.primary'
                                        }
                                        flex={1}
                                    >
                                        {bono.name} -{' '}
                                        {`${formatPriceByLocale(
                                            bono?.price,
                                            bono?.priceShape?.currency,
                                            bono?.priceShape?.locale,
                                        )}`}
                                    </Box>
                                    <Box
                                        color="text.secondary"
                                        minWidth={80}
                                        textAlign="center"
                                    >
                                        {bono.type && t(`BT.${bono.type}`)}
                                    </Box>
                                    {!bono.purchaseOnline ? (
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            color="text.secondary"
                                            minWidth={120}
                                            justifyContent="flex-end"
                                        >
                                            {t('bono.noOnline')}
                                        </Box>
                                    ) : (
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            color="success.main"
                                            minWidth={120}
                                            justifyContent="flex-end"
                                        >
                                            <span>
                                                {t('bono.onlinePurchase')}
                                            </span>
                                            &nbsp;
                                            <Check />
                                        </Box>
                                    )}
                                </Box>
                            ),
                            objToSelect: bono,
                        }))}
                    />
                ) : (
                    <SttNoResults />
                )}
            </Box>
        </Container>
    );
}
